import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { ChartDataPoint } from '../../types';

interface UsageChartProps {
  data: ChartDataPoint[];
  title?: string;
  height?: number;
}

export const UsageChart: React.FC<UsageChartProps> = ({ 
  data, 
  title = "Usage Over Time",
  height = 400 
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatCost = (value: number) => `$${value.toFixed(2)}`;
  const formatTokens = (value: number) => `${(value / 1000).toFixed(0)}k`;

  return (
    <div className="chart-container">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="date" 
            tickFormatter={formatDate}
            stroke="#6b7280"
            fontSize={12}
          />
          <YAxis 
            yAxisId="requests"
            orientation="left"
            stroke="#6b7280"
            fontSize={12}
          />
          <YAxis 
            yAxisId="cost"
            orientation="right"
            tickFormatter={formatCost}
            stroke="#6b7280"
            fontSize={12}
          />
          <Tooltip 
            labelFormatter={(label) => formatDate(label)}
            formatter={(value: number, name: string) => {
              if (name === 'cost') return [formatCost(value), 'Cost'];
              if (name === 'tokens') return [formatTokens(value), 'Tokens'];
              return [value, 'Requests'];
            }}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
          />
          <Legend />
          <Line
            yAxisId="requests"
            type="monotone"
            dataKey="requests"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
            name="Requests"
          />
          <Line
            yAxisId="cost"
            type="monotone"
            dataKey="cost"
            stroke="#10b981"
            strokeWidth={2}
            dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
            name="Cost"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};